// src/stores/map/mapTypes.ts
//
// Shared type definitions for the Zustand map store.
// Re-exports existing types from the Redux slice and defines new readonly
// variants used by the store interface.
//
// Consumers should import map-related types from this module rather than
// from `store/features/map/mapSlice` once the migration is complete.
import type { Geometry, Position } from "@turf/helpers";
import type { MapLayer } from "types/maps/entities";
import type { RigFracFocusAttribute } from "utils/color/focusColorSync/triggers";

import type { IMapResult } from "api/map";

import type { IColorPalette } from "models/colorPalette";
import type { EntityKind } from "models/entityKind";

// Re-export domain types that external consumers need
export type { IMapResult } from "api/map";
export type { MapLayer } from "types/maps/entities";
export type { Geometry, Position } from "@turf/helpers";

// ── Domain Interfaces ────────────────────────────────────────────────────────

export interface ISelectedWell {
  color: string;
  Uwi: string;
  group: string;
  /**
   * In group mode, the constituent well UWIs that make up this group entity.
   * Populated by useMapClickSelection when the clicked feature carries a non-empty
   * `group` property. Empty / absent in single (well) mode.
   */
  readonly memberUwis?: readonly string[];
  /**
   * For subordinate wells (`group === "leg" | "inj"`), the UWI of the parent well
   * whose colour this well mirrors. Legs carry a synthetic `legUwi` and inj wells
   * are auto-added, so neither is returned by the map filter endpoint — consumers
   * recolour them from the parent's filtered colour via this field.
   */
  readonly parentUwi?: string;
}

export type { IMapRequest } from "types/maps/request";

// ── Enums ────────────────────────────────────────────────────────────────────

export enum ConfirmationChoice {
  Confirm = "confirm",
  Cancel = "cancel",
  None = "none"
}

// ── Value Objects ────────────────────────────────────────────────────────────

export interface WellNetworkRequest {
  readonly wellIds: string[];
  readonly date?: Maybe<string>;
  readonly onlyPrimaryPath?: boolean;
  readonly hideConnectionsLessThanPercent?: number;
}

/** Per-layer Rig/Frac attribute filter selections (EVA-63). Empty/absent = no constraint. */
export interface RigFracLayerFilter {
  readonly operator?: string[];
  readonly county?: string[];
  readonly basin?: string[];
  readonly subBasin?: string[];
  readonly country?: string[];
  readonly stateProvince?: string[];
  readonly locationType?: string[];
  /**
   * EVA-107. May contain `RIG_FRAC_NO_VALUE_OPTION` ("n/a"), which selects the
   * features carrying no `rigTrajectory` tag at all — see buildRigFracFilter.
   */
  readonly rigTrajectory?: string[];
}

export interface LayerStyleSetting {
  readonly color?: string;
  /** Fraction 0-1. */
  readonly opacity?: number;
  readonly thickness?: number;
}

export interface MapDisplaySettings {
  readonly wellLineThickness: number;
  readonly wellDotThickness: number;
  readonly wellLabelSize: number;
  readonly facilityLabelSize: number;
  readonly facilityIconSize: number;
  readonly shapefileDetailsLabelSize: number;
  readonly shapefileNameLabelSize: number;
  readonly sectionLabelSize: number;
  readonly layerZoomSettings: Readonly<
    Record<string, { minzoom?: number; maxzoom?: number }>
  >;
  readonly labelDensity: Readonly<Record<string, number>>;
  // Optional: setDisplaySettings replaces displaySettings wholesale with no
  // defaults merge, so the field can be absent — a required type would lie at
  // runtime and reads would throw.
  readonly layerFilterSettings?: Readonly<Record<string, RigFracLayerFilter>>;
}

// Rig/frac icon sizes are session-only client state that lives on the Zustand
// map store, not in the frozen Redux mapSlice copy of MapDisplaySettings. Keep
// them off the shared type so the Redux slice never carries a dead mirror.
export interface MapStoreDisplaySettings extends MapDisplaySettings {
  readonly rigIconSize: number;
  readonly fracIconSize: number;
  /**
   * Per-layer set of currently hidden attribute values, keyed by layerId. These
   * values are filtered off the map for the layer's active color-by attribute.
   * Relocated into displaySettings (EVA-63 FR-017) so it rides the existing
   * whole-state View capture/restore instead of needing its own wiring.
   */
  readonly hiddenRigFracValues: Readonly<Record<string, readonly string[]>>;
  /**
   * Per-layer remembered colouring source (last applied palette or single
   * colour), keyed by layerId. Reused as the auto-assignment source when the
   * layer's Color By attribute changes so the choice sticks across group-by
   * switches. Relocated into displaySettings (EVA-63 FR-017) so it rides the
   * existing whole-state View capture/restore instead of needing its own wiring.
   */
  readonly rigFracColorSource: Readonly<Record<string, RigFracColorSource>>;
  /**
   * Label text size for each US/Texas grid label layer, keyed by layerId. An
   * absent key means the layer still uses the size it was created with
   * (GRID_LABEL_DEFAULT_SIZES), so the record stays empty until the user picks
   * a size. The Canadian grid labels are not keyed here — they keep the single
   * `sectionLabelSize` setting above.
   */
  readonly gridLabelSizes: Readonly<Record<string, number>>;
  /**
   * User style (colour/opacity/thickness) per layer name (EVA-351). Only the
   * base well layer is written today. Read through `resolveBaseWellStyle`,
   * never raw.
   */
  readonly layerStyleSettings: Readonly<Record<string, LayerStyleSetting>>;
}

export interface FlyToSpecificPoint {
  readonly lat: number;
  readonly lng: number;
  readonly zoom: number;
}

/**
 * Bounding box the map camera should fit, in lng/lat. Structurally the same as
 * the map component's `Extent`, redeclared here because stores must not import
 * from components.
 */
export interface FlyToExtent {
  readonly minx: number;
  readonly miny: number;
  readonly maxx: number;
  readonly maxy: number;
}

/**
 * How a fly-to should be framed. Omitted fields keep the consumer's defaults,
 * so a producer that does not care is unaffected — only the caller that needs
 * different framing carries it.
 */
export interface FlyToExtentOptions {
  readonly maxZoom?: number;
  /** A number for uniform padding, or per-side when one edge is obstructed. */
  readonly padding?: number | mapboxgl.PaddingOptions;
  /** Pads the side the well card covers, so the target lands beside it. */
  readonly clearWellCard?: boolean;
}

/**
 * A queued camera fit plus the time it was issued. The command is never cleared
 * on consumption: each map instance remembers the newest `at` it has fitted and
 * takes only newer ones, so two map widgets both fit the same pick. `at` is
 * also the ownership check — useMapNavigation's consume effect ignores a
 * command stamped before its map instance mounted, which is what stops a pick
 * made in a mapless layout from firing whenever a map next mounts.
 */
export interface FlyToExtentCommand {
  readonly extent: FlyToExtent;
  readonly at: number;
  readonly options?: FlyToExtentOptions;
}

export interface INetworkDepthLegend {
  legendItems: { title: string; color: string }[];
  visible: boolean;
  title: string;
}

export interface Envelope {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/** The rig/frac layer drilled into for the Color By details panel. */
export interface SelectedRigFracLayer {
  readonly layerId: string;
  /** The layer name, which equals the Mapbox layer id ("Rigs" / "Frac Crews"). */
  readonly name: string;
}

/**
 * Remembered auto-assignment source for a rig/frac layer — the last palette or
 * single colour the user applied. Used as the colouring source when the layer's
 * Color By attribute changes so the choice carries over to the new domain.
 * Session-only; never persisted.
 */
export type RigFracColorSource =
  | { readonly kind: "palette"; readonly palette: IColorPalette }
  | { readonly kind: "solid"; readonly color: string };

// ── State Interface ──────────────────────────────────────────────────────────

export interface MapState {
  // ── Transaction / Map Results ────────────────────────────────────────
  readonly txnId: IMapResult;

  // ── Entity Selection ────────────────────────────────────────────────
  readonly selectedWells: Readonly<Record<string, ISelectedWell>>;
  readonly selectedFacilities: Readonly<Record<string, ISelectedWell>>;
  readonly attentionWells: readonly string[];
  /**
   * UWIs highlighted by a Jump to Location well-list jump. Separate from
   * `attentionWells`, which is ephemeral hover state cleared on every camera
   * `move`: a jump's highlight must survive the camera move the jump itself
   * triggers, and must not be read as "the hovered well" by chart code that
   * indexes `attentionWells[0]`. Both render on ATTENTION_WELL_LAYER as a union.
   */
  readonly jumpHighlightWells: readonly string[];
  readonly typeLogWells: ReadonlyArray<ISelectedWell>;
  /** Rig/frac layer drilled into for the Color By details panel (null = list view). */
  readonly selectedRigFracLayer: Maybe<SelectedRigFracLayer>;
  /**
   * `id` of the rig/frac feature currently hovered on the map, or null when
   * nothing is hovered. Written by the hover-tooltip adapter and read by the
   * icon-size adapter, which owns applying the enlarge expression so hover
   * enlargement and the user size multiplier compose. Session-only.
   */
  readonly hoveredRigFracId: Maybe<string>;
  /**
   * The rig/frac attribute the active well focus has pushed onto every rig/frac
   * layer's Color By, or undefined when no focus has pushed one this session.
   *
   * Session-only and deliberately NOT in displaySettings: the layer's own
   * `colorByField` is project state shared with every collaborator, and a
   * focus click must not rewrite what a teammate sees (nor silently do nothing
   * for a view-only user, who cannot write it at all). Keeping the focus's
   * answer here means it overlays the saved value for this session and leaves
   * the saved value untouched.
   */
  readonly rigFracFocusColorBy: Maybe<RigFracFocusAttribute>;
  /**
   * Layers whose Color By the user has picked by hand since the last focus
   * push — those read their own saved `colorByField` again and ignore the
   * overlay above. Cleared whenever the focus pushes a new attribute, so the
   * next focus change takes every layer back with it.
   */
  readonly rigFracColorByDetached: Readonly<Record<string, true>>;
  /**
   * Per-layer available filter options (EVA-63 revision), keyed by layerId then
   * field. Derived — NOT persisted, NOT in displaySettings: recomputed by the
   * rig/frac layer-filter adapter from the features currently on the map, so a
   * dropdown offers only values still reachable under the other active selections
   * (cascade), never the whole market domain. Empty until the map has features.
   */
  readonly rigFracFilterOptions: Readonly<
    Record<string, Readonly<Record<string, readonly string[]>>>
  >;

  // ── Display Settings ────────────────────────────────────────────────
  readonly displaySettings: MapStoreDisplaySettings;
  readonly layers: readonly MapLayer[];
  readonly baseLayerResourcePlays: readonly string[] | undefined;
  /**
   * The base well layer's OWN filterId (EVA-35), posted independent of the
   * well-list filter — see useBaseLayerFilterSync. Tiles and the resource-play
   * dropdown reference this id. Undefined until the first post resolves; once
   * set it is never cleared back to undefined by a later failed repost (a
   * failed repost keeps the layer working under the last-known-good id).
   */
  readonly baseLayerFilterId: Maybe<string>;
  readonly ipdbLayersLoaded: {
    readonly geoModel: boolean;
    readonly controlWells: boolean;
  };
  /** True while the IPDB geo-model load is in-flight; false once settled (success or error). */
  readonly geoModelLoading: boolean;
  readonly isDebuggingMap: boolean;

  // ── Viewport / Navigation ──────────────────────────────────────────
  readonly mapState: {
    readonly lat: number;
    readonly lng: number;
    readonly zoom: number;
  };
  readonly viewLock: boolean;
  readonly mapExtent: GeoJSON.Polygon;
  /**
   * True from the moment a camera starts moving until every camera has
   * stopped, whether the move came from the user or from the app flying the
   * camera somewhere. Published so consumers outside the map can wait for a
   * settled viewport without subscribing to mapbox events. It says nothing
   * about tiles or style loading — only where the cameras are.
   *
   * Like the rest of this store it is global, not per map widget. It is
   * derived from mapMoverCount rather than written directly, so with two maps
   * on screen the second one stopping cannot speak for the first one, which is
   * still moving.
   */
  readonly isMapMoving: boolean;
  /**
   * How many map widgets are moving right now. Each widget claims one count
   * when its camera starts and releases it when the camera stops or the widget
   * goes away; nothing else reads it.
   */
  readonly mapMoverCount: number;
  readonly flyToGroup: Maybe<string>;
  readonly flyToSpecificPoint: Maybe<FlyToSpecificPoint>;
  readonly flyToExtent: Maybe<FlyToExtentCommand>;
  readonly attentionPolygon: Maybe<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
  readonly exploitableArea: readonly Geometry[];

  // ── XDA Cross-Section ──────────────────────────────────────────────
  readonly xdaInterceptData: {
    readonly line: readonly Position[];
    readonly points: readonly Position[];
  };
  readonly xdaLineRequest: {
    readonly line: readonly Position[];
    readonly wells: readonly string[];
  };

  // ── Midstream Network Visualization ────────────────────────────────
  readonly networkDepthLegend: INetworkDepthLegend;
  readonly showNetworkOptions: boolean;
  readonly highlightContributingWellsForFacility: Maybe<string>;
  readonly wellNetworkMapRequest: WellNetworkRequest;
  readonly isWellNetworkFilterActivated: boolean;
  readonly networkEnvelope: Maybe<Envelope>;
  readonly salesEnvelope: Maybe<Envelope>;

  // ── Series Size Confirmation ───────────────────────────────────────
  readonly hasToConfirmSize: boolean;
  readonly facilityHasToConfirmSize: boolean;
  readonly facilityOverrideSizeLimit: boolean;
  readonly wellsConfirmationChoice: Maybe<ConfirmationChoice>;
  readonly facilityConfirmationChoice: Maybe<ConfirmationChoice>;
}

// ── Actions Interface ────────────────────────────────────────────────────────

export interface MapActions {
  // ── Transaction ─────────────────────────────────────────────────────
  /** Set the combined well + facility map result. Includes skip-update guard. */
  setTxnId: (result: IMapResult) => void;

  // ── Entity Selection ────────────────────────────────────────────────
  setSelectedWells: (wells: Record<string, ISelectedWell>) => void;
  setSelectedFacilities: (facilities: Record<string, ISelectedWell>) => void;
  setSelectedEntities: (
    entityKind: EntityKind,
    entities: Record<string, ISelectedWell>
  ) => void;
  /** Structural-equality guarded — skips update if contents match. */
  setAttentionWells: (uwis: string[]) => void;
  /**
   * Structural-equality guarded, same as `setAttentionWells`. Cleared by a new
   * jump, by "Clear all", and by the user's next map gesture — a movestart or
   * click carrying an `originalEvent`. NOT by the jump's own fitBounds, which
   * fires no originalEvent, nor by chart hover.
   */
  setJumpHighlightWells: (uwis: string[]) => void;
  setTypeLogWells: (wells: ISelectedWell[]) => void;
  /** Set (or clear with null) the rig/frac Color By drill-in selection. */
  setSelectedRigFracLayer: (layer: Maybe<SelectedRigFracLayer>) => void;
  /** Replace the hidden-value set for a rig/frac layer (session-only visibility). */
  setHiddenRigFracValues: (layerId: string, values: string[]) => void;
  /** Clear every layer's hidden-value set (project switch). */
  clearHiddenRigFracValues: () => void;
  /** Remember the applied palette / single colour for a rig/frac layer (session-only). */
  setRigFracColorSource: (layerId: string, source: RigFracColorSource) => void;
  /** Clear every layer's remembered colouring source (project switch). */
  clearRigFracColorSource: () => void;
  /** Set (or clear with null) the hovered rig/frac feature id for icon enlargement. */
  setHoveredRigFracId: (id: Maybe<string>) => void;
  /** Push a focus-driven Color By onto every rig/frac layer; re-attaches detached layers. */
  setRigFracFocusColorBy: (field: RigFracFocusAttribute) => void;
  /** Detach one layer from the focus-driven Color By (the user picked its own). */
  detachRigFracColorBy: (layerId: string) => void;
  /** Re-attach one layer to the focus-driven Color By (rolls a failed pick back). */
  attachRigFracColorBy: (layerId: string) => void;
  /** Drop the focus-driven Color By and every detachment (project switch). */
  clearRigFracFocusColorBy: () => void;

  // ── Display Settings ────────────────────────────────────────────────
  /** Accepts either a full settings object or a functional updater
   *  `(current) => next` — the functional form avoids the stale-closure lost-
   *  update hazard for callers that read `displaySettings` at render time and
   *  spread a single field into it later (EVA-63). */
  setDisplaySettings: (
    settings:
      | MapStoreDisplaySettings
      | ((current: MapStoreDisplaySettings) => MapStoreDisplaySettings)
  ) => void;
  /** Apply a View's saved display settings as a per-category merge over
   *  current settings (EVA-63). No-op when saved is absent (pre-feature View). */
  applyViewDisplaySettings: (saved?: Partial<MapStoreDisplaySettings>) => void;
  /** Clamps value to [0, 1]. */
  setLabelDensity: (layer: string, value: number) => void;
  /** Functional update — merges a single field for one layer's Rig/Frac filter selections without clobbering siblings (EVA-63). Empty array omits the field (show all). */
  setLayerFilter: (
    layerId: string,
    field: keyof RigFracLayerFilter,
    values: string[]
  ) => void;
  /** Replace one layer's derived available filter options (EVA-63 revision). Session-only, written by the rig/frac layer-filter adapter. */
  setRigFracFilterOptions: (
    layerId: string,
    options: Readonly<Record<string, readonly string[]>>
  ) => void;
  setLayers: (layers: MapLayer[]) => void;
  setBaseLayerResourcePlays: (plays: string[] | undefined) => void;
  /** Set (or clear with undefined) the base well layer's own filterId. */
  setBaseLayerFilterId: (filterId: Maybe<string>) => void;
  setIpdbGeoModelLoaded: (loaded: boolean) => void;
  setIpdbControlWellsLoaded: (loaded: boolean) => void;
  /** Set to true when a geo-model load begins; false when it settles (success or error). */
  setGeoModelLoading: (loading: boolean) => void;
  setIsDebuggingMap: (debugging: boolean) => void;

  // ── Viewport / Navigation ──────────────────────────────────────────
  setMapState: (state: { lat: number; lng: number; zoom: number }) => void;
  setViewLock: (locked: boolean) => void;
  setMapExtent: (extent: GeoJSON.Polygon) => void;
  beginMapMove: () => void;
  endMapMove: () => void;
  setFlyToGroup: (group: Maybe<string>) => void;
  setFlyToSpecificPoint: (point: Maybe<FlyToSpecificPoint>) => void;
  /**
   * Queues a camera fit, stamped with a strictly increasing issue time;
   * `undefined` clears it.
   */
  setFlyToExtent: (extent: Maybe<FlyToExtent>, options?: FlyToExtentOptions) => void;
  setAttentionPolygon: (polygon: Maybe<GeoJSON.Polygon | GeoJSON.MultiPolygon>) => void;
  setExploitableArea: (area: Geometry[]) => void;

  // ── XDA Cross-Section ──────────────────────────────────────────────
  setXdaInterceptData: (data: { line: Position[]; points: Position[] }) => void;
  setXdaLineRequest: (request: { line: Position[]; wells: string[] }) => void;

  // ── Midstream Network ──────────────────────────────────────────────
  setNetworkDepthLegend: (legend: INetworkDepthLegend) => void;
  clearNetworkDepthLegend: () => void;
  setShowNetworkOptions: (show: boolean) => void;
  setHighlightContributingWellsForFacility: (facilityId: string) => void;
  clearHighlightContributingWellsForFacility: () => void;
  setWellNetworkMapRequest: (request: WellNetworkRequest) => void;
  clearWellNetworkMapRequest: () => void;
  setWellNetworkFilterActivated: (activated: boolean) => void;
  setNetworkEnvelope: (envelope: Maybe<Envelope>) => void;
  setSalesEnvelope: (envelope: Maybe<Envelope>) => void;

  // ── Series Size Confirmation ───────────────────────────────────────
  setHasToConfirmSize: (confirm: boolean) => void;
  setFacilityHasToConfirmSize: (confirm: boolean) => void;
  setFacilityOverrideSizeLimit: (override: boolean) => void;
  setWellsConfirmationChoice: (choice: Maybe<ConfirmationChoice>) => void;
  setFacilityConfirmationChoice: (choice: Maybe<ConfirmationChoice>) => void;

  // ── Lifecycle ──────────────────────────────────────────────────────
  /** Reset the entire store to initial state (project switch, signout). */
  reset: () => void;
}

// ── Combined Store Type ──────────────────────────────────────────────────────

export type MapStore = MapState & MapActions;
